import { CartUpdateEvent, ThemeEvents } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

/**
 * JBC CUSTOMIZATION: keep the X3 Force Premium subscription dependent on an
 * eligible X3 Force product, and enforce a max quantity of one subscription.
 */

const BASE_PRODUCT_HANDLES = ['x3-force-bar', 'x3-force-smart-gym'];
const PREMIUM_APP_HANDLE = 'x3-force-premium-app';
const CONFIGURATOR_HANDLES = ['x3-configurator', 'x3-open-box-configurator'];
const SOURCE_ID = 'jbc-cart-conditional-removal';

let isSyncingPremiumApp = false;

function isForceConfiguratorItem(item) {
  return CONFIGURATOR_HANDLES.includes(item.handle) && item.properties?._x3_force === 'true';
}

function hasBaseProduct(items) {
  return items.some((item) => BASE_PRODUCT_HANDLES.includes(item.handle) || isForceConfiguratorItem(item));
}

function getPremiumAppItems(items) {
  return items.filter((item) => item.handle === PREMIUM_APP_HANDLE);
}

function isCartResponse(value) {
  return Array.isArray(value?.items) && typeof value?.item_count === 'number';
}

function getSectionIds() {
  return Array.from(document.querySelectorAll('cart-items-component'))
    .map((component) => component.dataset.sectionId)
    .filter(Boolean)
    .join(',');
}

async function fetchCart() {
  const root = window.Shopify?.routes?.root || '/';
  const normalizedRoot = root.endsWith('/') ? root : `${root}/`;
  const response = await fetch(`${normalizedRoot}cart.js`);

  if (!response.ok) {
    throw new Error(`Failed to fetch cart: ${response.statusText}`);
  }

  return response.json();
}

function getCartItemCount(cart) {
  if (typeof cart?.item_count === 'number') {
    return cart.item_count;
  }

  if (Array.isArray(cart?.items)) {
    return cart.items.reduce((count, item) => count + (item.quantity || 0), 0);
  }

  return 0;
}

function dispatchCartUpdate(cart) {
  document.dispatchEvent(
    new CartUpdateEvent(cart, SOURCE_ID, {
      itemCount: getCartItemCount(cart),
      source: SOURCE_ID,
      sections: cart?.sections || {},
    })
  );
}

function buildPremiumAppUpdates(items) {
  const premiumItems = getPremiumAppItems(items);
  const updates = {};

  if (premiumItems.length === 0) {
    return updates;
  }

  if (!hasBaseProduct(items)) {
    premiumItems.forEach((item) => {
      updates[item.key] = 0;
    });

    return updates;
  }

  premiumItems.forEach((item, index) => {
    if (index === 0 && item.quantity !== 1) {
      updates[item.key] = 1;
    } else if (index > 0) {
      updates[item.key] = 0;
    }
  });

  return updates;
}

async function updateCart(updates) {
  const body = JSON.stringify({
    updates,
    sections: getSectionIds(),
    sections_url: window.location.pathname,
  });

  const response = await fetch(Theme.routes.cart_update_url, fetchConfig('json', { body }));

  if (!response.ok) {
    throw new Error(`Failed to update cart: ${response.statusText}`);
  }

  const cart = await response.json();

  if (cart.errors) {
    throw new Error(typeof cart.errors === 'string' ? cart.errors : 'Failed to update cart');
  }

  return cart;
}

async function syncPremiumAppItems(cart) {
  if (isSyncingPremiumApp || !Array.isArray(cart?.items)) {
    return;
  }

  const updates = buildPremiumAppUpdates(cart.items);

  if (Object.keys(updates).length === 0) {
    return;
  }

  isSyncingPremiumApp = true;

  try {
    const updatedCart = await updateCart(updates);
    dispatchCartUpdate(updatedCart);
  } catch (error) {
    console.error('[Cart Conditional Removal] Error syncing premium app item:', error);
  } finally {
    isSyncingPremiumApp = false;
  }
}

async function syncPremiumAppItemsFromCurrentCart() {
  if (isSyncingPremiumApp) {
    return;
  }

  try {
    await syncPremiumAppItems(await fetchCart());
  } catch (error) {
    console.error('[Cart Conditional Removal] Error checking cart:', error);
  }
}

document.addEventListener(ThemeEvents.cartUpdate, (event) => {
  const cart = event.detail?.resource || event.detail?.data;

  if (isCartResponse(cart)) {
    syncPremiumAppItems(cart);
  } else {
    syncPremiumAppItemsFromCurrentCart();
  }
});

['cart:updated', 'shopify:cart:updated'].forEach((eventName) => {
  document.addEventListener(eventName, syncPremiumAppItemsFromCurrentCart);
});
